import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Truck, Users, Award, Phone, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { BRAND } from "../App";

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="bg-black text-white pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1570733117311-d990c3816c47?w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black to-black/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E31E24] mb-4">About Us</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-4">
            Quality Towing<br />
            <span className="text-[#E31E24]">& Transport</span>
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">Your trusted partner for vehicle transportation and quality pre-owned vehicles since 2016.</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E31E24] mb-3">Our Story</p>
              <h2 className="text-3xl sm:text-4xl font-black uppercase mb-6">Reliable Service You Can Count On</h2>
              <div className="space-y-4 text-black/80">
                <p>
                  I'm <strong>Shabi</strong>, the owner and operator of Quality Towing & Transport. I started selling cars around 2016-2017 with a simple mission: providing quality vehicles at affordable prices for hardworking people.
                </p>
                <p>
                  Over the years, we've expanded to include professional vehicle transportation services. Whether you need a car moved across town or across state lines, we have the equipment, experience, and dedication to get the job done right.
                </p>
                <p>
                  We use a Ford F-150 pickup truck paired with a 20-foot single-car hauling trailer equipped with a winch — perfect for safely transporting vehicles that run and drive, as well as those that need loading assistance.
                </p>
                <p>
                  Our commitment is simple: honest service, fair prices, and treating every customer's vehicle like our own.
                </p>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href={BRAND.phoneHref} className="bg-[#E31E24] text-white font-bold uppercase px-6 py-3 hover:bg-[#C01A1F] flex items-center gap-2">
                  <Phone size={18} /> Call Shabi: {BRAND.phone}
                </a>
                <Link to="/get-quote" className="border border-black font-bold uppercase px-6 py-3 hover:bg-black hover:text-white text-center">
                  Request Service
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <img src={BRAND.logoPromo} alt="Quality Towing & Transport" className="w-full max-w-lg mx-auto" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E31E24] mb-3">Our Values</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase">What We Stand For</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Safe & Insured", desc: "Every vehicle is transported safely with proper equipment and care." },
              { icon: Truck, title: "Reliable Equipment", desc: "Professional-grade Ford F-150 and 20-foot trailer with winch." },
              { icon: Users, title: "Customer First", desc: "Honest communication and fair pricing on every transport job." },
              { icon: Award, title: "Years of Experience", desc: "Trusted by hundreds of customers since 2016." },
            ].map((v, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}
                className="bg-white p-8 border-t-4 border-[#E31E24]">
                <v.icon size={36} className="text-[#E31E24] mb-4" />
                <h3 className="font-black uppercase mb-2">{v.title}</h3>
                <p className="text-sm text-black/70 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#E31E24] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-black uppercase mb-4">Ready to Work with Us?</h2>
          <p className="text-white/90 text-lg mb-8">Experience the difference of working with a dedicated local business.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-quote" className="bg-black text-white font-bold uppercase px-8 py-4 hover:bg-white hover:text-black flex items-center justify-center gap-2">
              Request Transport <ArrowRight size={20} />
            </Link>
            <Link to="/cars-for-sale" className="bg-white text-black font-bold uppercase px-8 py-4 hover:bg-black hover:text-white">
              Browse Cars
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
